import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Search } from "lucide-react";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { glossary } from "@/data/glossary";
import { usePageMeta } from "@/lib/api";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function GlossaryPage() {
  usePageMeta(
    "ESG & Sustainability Glossary | SarvaRitam — Plain-language ESG definitions",
    "Plain-language definitions of ESG terms: BRSR, Scope 1 2 3, materiality, TCFD, TNFD, SFDR, PAI, DNSH and more."
  );
  const [letter, setLetter] = useState("All");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);

  const filtered = useMemo(() => {
    return glossary
      .filter((g) => {
        const inL = letter === "All" || g.term[0].toUpperCase() === letter;
        const q = query.trim().toLowerCase();
        return inL && (!q || (g.term + g.def).toLowerCase().includes(q));
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [letter, query]);

  const activeLetters = new Set(glossary.map((g) => g.term[0].toUpperCase()));

  return (
    <>
      <PageHero
        eyebrow="ESG glossary"
        title={[<>ESG jargon, <span className="serif-it text-gold">translated.</span></>]}
        intro="Plain-language definitions of the terms you'll meet in ESG conversations — from BRSR to DNSH."
      />
      <section className="bg-offwhite text-charcoal sec" data-testid="glossary-page">
        <div className="wrap">
          <div className="flex flex-col gap-6 mb-12">
            <div className="relative max-w-md w-full">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-forest/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search terms…"
                className="w-full bg-white/70 border rule-light pl-11 pr-4 py-3.5 text-[14px] text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-forest"
                data-testid="glossary-search-input"
                aria-label="Search glossary"
              />
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="glossary-letter-filter">
              <button onClick={() => setLetter("All")} className={`px-3.5 py-2 text-[12px] font-bold rounded-full border transition-all ${letter === "All" ? "bg-forest text-offwhite border-forest" : "border-forest/25 text-forest/70 hover:border-forest"}`} data-testid="glossary-letter-all">
                All
              </button>
              {LETTERS.map((l) => {
                const has = activeLetters.has(l);
                return (
                  <button
                    key={l}
                    disabled={!has}
                    onClick={() => setLetter(l)}
                    className={`w-9 py-2 text-[12px] font-bold rounded-full border transition-all ${letter === l ? "bg-forest text-offwhite border-forest" : has ? "border-forest/25 text-forest/70 hover:border-forest" : "border-transparent text-charcoal/20 cursor-default"}`}
                    data-testid={`glossary-letter-${l}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-dashed rule-light p-16 text-center" data-testid="glossary-empty-state">
              <p className="font-display font-semibold text-xl text-charcoal/70">No terms found.</p>
              <button onClick={() => { setQuery(""); setLetter("All"); }} className="btn-forest mt-6">Clear filters</button>
            </div>
          ) : (
            <div className="border rule-light divide-y divide-[rgba(18,55,42,0.14)] bg-white/60" data-testid="glossary-list">
              {filtered.map((g) => (
                <div key={g.term}>
                  <button
                    onClick={() => setOpen(open === g.term ? null : g.term)}
                    className="w-full flex items-center justify-between gap-6 px-6 sm:px-9 py-5 text-left hover:bg-white/60 transition-colors"
                    data-testid={`glossary-term-${g.term.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    aria-expanded={open === g.term}
                  >
                    <span className="flex items-center gap-4">
                      <span className="font-mono text-[11px] text-gold w-6">{g.term[0]}</span>
                      <span className="font-display font-semibold text-[17px] text-charcoal">{g.term}</span>
                      <span className="hidden sm:inline text-[10.5px] font-mono uppercase tracking-widest2 text-sage">{g.cat}</span>
                    </span>
                    <ChevronDown size={16} className={`text-forest/50 transition-transform duration-300 shrink-0 ${open === g.term ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {open === g.term && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
                        <p className="px-6 sm:px-9 pb-7 pl-6 sm:pl-[76px] text-[15px] text-charcoal/70 leading-relaxed max-w-3xl" data-testid={`glossary-definition-${g.term.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {g.def}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
          <p className="text-[12px] text-charcoal/45 mt-8" data-testid="glossary-count">
            {filtered.length} term{filtered.length === 1 ? "" : "s"} · glossary is extendable through the content structure.
          </p>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
